import { useQuery } from 'react-query';
import { QueryId, STALE_TIME } from '@/integrations/content/utils';
import formatUrlSearchParameters from '@/libraries/formatters/format-url-search-parameters';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';

type UseQuerySearchDestinationHierarchyData =
  Content.Endpoints.SearchDestinationHierarchy;

interface UseQuerySearchDestinationHierarchyOptions {
  enabled?: boolean;
  initialData?: UseQuerySearchDestinationHierarchyData;
}

interface UseQuerySearchDestinationHierarchyParameters {
  campaignId?: string;
  departureIds?: string;
  pattern: string;
  patternMatch?: Content.Params.SearchDestinationHierarchy['PatternMatch'];
  periodType?: Content.Components['PeriodType'];
}

const useQuerySearchDestinationHierarchy = (
  parameters: UseQuerySearchDestinationHierarchyParameters,
  options?: UseQuerySearchDestinationHierarchyOptions,
) => {
  const locale = getLocale();

  const query = useQuery<UseQuerySearchDestinationHierarchyData>(
    [parameters, QueryId.SEARCH_DESTINATION_HIERARCHY, locale],
    async () => {
      const baseUrl = '/api/content-destinations/search-destination-hierarchy';
      const queryParameters = formatUrlSearchParameters(parameters);

      const fetchResponse = await fetch(
        `${baseUrl}?${queryParameters.toString()}`,
        {
          headers: {
            ['Accept-Language']: getCulture(locale),
            ['Content-Type']: 'application/json',
          },
          method: 'GET',
        },
      )
        .then(response => {
          if (response.ok) return response.json();

          throw new Error(`${response.status} - ${response.statusText}`);
        })
        .catch(error => {
          console.error('hooks.useQuerySearchDestinationHierarchy', error);

          throw error;
        });

      return fetchResponse;
    },
    {
      enabled: !!parameters.pattern && !!options?.enabled,
      initialData: options?.initialData,
      keepPreviousData: true,
      staleTime: STALE_TIME,
    },
  );

  return query;
};

export default useQuerySearchDestinationHierarchy;
